import { useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { copy } from "../copy";
import Icon, { type IconName } from "../components/Icon";
import { MAX_UPLOAD_BYTES } from "../contest/upload";
import type { NewContest, Options } from "./api";
import { tierCopy, TIER_LABEL, type Tier } from "../tiers";
import { ghostButton, inputClass } from "./ui";

// The contest fields the New contest and Edit contest pages share. Seats is
// held as typed and sent as a number.

export const MIN_SEATS = 2;
export const MAX_SEATS = 300;
export const MAX_COVER_BYTES = 10 * 1024 * 1024;
export const COVER_TYPES = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
export const FILE_TYPES = [".mp3", ".wav", ".zip"];

const hasType = (file: File, types: string[]) => types.some((ext) => file.name.toLowerCase().endsWith(ext));
export const coverOK = (file: File) => hasType(file, COVER_TYPES) && file.size > 0 && file.size <= MAX_COVER_BYTES;
export const sampleOK = (file: File) => hasType(file, FILE_TYPES) && file.size > 0 && file.size <= MAX_UPLOAD_BYTES;

export const labelClass = "mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-chrome-dim";
export const hintClass = "mt-1.5 text-[12px] text-chrome-dim";

export type ContestFormValues = Omit<NewContest, "seats"> & { seats: string };

export const EMPTY_CONTEST_FORM: ContestFormValues = {
  name: "",
  description: "",
  genre: "",
  challenge: "",
  duration: "",
  seats: "8",
  voting: "public",
  publish: true,
};

// contestBody is what the create and edit endpoints read: trimmed text and
// seats as a number. There is no money in it.
export function contestBody(form: ContestFormValues): NewContest {
  const body: NewContest = {
    name: form.name.trim(),
    description: form.description.trim(),
    genre: form.genre,
    challenge: form.challenge,
    duration: form.duration,
    seats: Number(form.seats),
    voting: form.voting,
    publish: form.publish,
  };
  // A tier is only sent when the creator picked one; the server stores
  // Standard otherwise, and the edit endpoint never reads it.
  if (form.tier) body.tier = form.tier;
  return body;
}

type Setter = <K extends keyof ContestFormValues>(key: K, value: ContestFormValues[K]) => void;

export function ContestFields({
  form,
  set,
  options,
  showPublish,
  disabled = false,
}: {
  form: ContestFormValues;
  set: Setter;
  options: Options;
  showPublish: boolean;
  disabled?: boolean;
}) {
  const t = copy.admin.newContest;
  const select = (key: "genre" | "challenge" | "duration", label: string, values: string[]) => {
    // An edited contest may hold a value no longer on the list; keep it
    // selectable so the form shows what is stored.
    const list = form[key] && !values.includes(form[key]) ? [form[key], ...values] : values;
    return (
      <div>
        <label htmlFor={`new-${key}`} className={labelClass}>
          {label}
        </label>
        <select
          id={`new-${key}`}
          required
          disabled={disabled}
          value={form[key]}
          onChange={(e) => set(key, e.target.value)}
          className={`${inputClass} cursor-pointer disabled:cursor-not-allowed disabled:opacity-60`}
        >
          <option value="">{t.choose}</option>
          {list.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <>
      <Card title={t.details} icon="quillPen">
        <div>
          <label htmlFor="new-name" className={labelClass}>
            {t.name}
          </label>
          <input
            id="new-name"
            required
            maxLength={80}
            disabled={disabled}
            value={form.name}
            placeholder={t.namePlaceholder}
            onChange={(e) => set("name", e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="new-description" className={labelClass}>
            {t.description}
          </label>
          <textarea
            id="new-description"
            rows={5}
            maxLength={2000}
            disabled={disabled}
            value={form.description}
            placeholder={t.descriptionPlaceholder}
            onChange={(e) => set("description", e.target.value)}
            className={`${inputClass} h-auto py-2.5 leading-relaxed`}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {select("genre", t.genre, options.genres)}
          {select("challenge", t.challenge, options.challenges)}
          {select("duration", t.duration, options.durations)}
        </div>
      </Card>

      <Card title={t.rules} icon="team">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="new-seats" className={labelClass}>
              {t.seats}
            </label>
            <input
              id="new-seats"
              type="number"
              required
              min={MIN_SEATS}
              max={MAX_SEATS}
              inputMode="numeric"
              disabled={disabled}
              value={form.seats}
              onChange={(e) => set("seats", e.target.value)}
              className={`${inputClass} bf-tabular`}
            />
            <p className={hintClass}>{t.seatsHint}</p>
          </div>
          <fieldset disabled={disabled}>
            <legend className={labelClass}>{t.voting}</legend>
            <div role="radiogroup" aria-label={t.voting} className="flex gap-2">
              {(["public", "judges"] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={form.voting === v}
                  onClick={() => set("voting", v)}
                  className={`bf-focusable h-10 flex-1 rounded-md px-3 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors disabled:cursor-not-allowed ${
                    form.voting === v ? "bg-gold text-ground" : "border border-ground-edge bg-ground text-chrome-dim hover:text-chrome"
                  }`}
                >
                  {t.votingOptions[v]}
                </button>
              ))}
            </div>
            <p className={hintClass}>{t.votingHint[form.voting]}</p>
          </fieldset>
        </div>
        {showPublish && options.tiers.length > 0 && (
          <div>
            <label htmlFor="new-tier" className={labelClass}>
              {tierCopy.label}
            </label>
            <select
              id="new-tier"
              disabled={disabled}
              value={form.tier ?? "standard"}
              onChange={(e) => set("tier", e.target.value as Tier)}
              className={`${inputClass} cursor-pointer disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {options.tiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tierCopy.option(tier.id, tier)}
                </option>
              ))}
            </select>
            <p className={hintClass}>{tierCopy.hint[form.tier ?? "standard"]}</p>
          </div>
        )}
        {showPublish && (
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-ground-edge bg-ground px-4 py-3">
            <input
              type="checkbox"
              checked={form.publish}
              onChange={(e) => set("publish", e.target.checked)}
              className="mt-0.5 h-4 w-4 cursor-pointer accent-money"
            />
            <span>
              <span className="block text-[13px] font-bold text-chrome">{t.publish}</span>
              <span className="block text-[12px] text-chrome-dim">{t.publishHint}</span>
            </span>
          </label>
        )}
      </Card>
    </>
  );
}

export function Card({ title, icon, children }: { title: string; icon: IconName; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-ground-edge bg-ground-raised p-5 md:p-6">
      <h2 className="mb-5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-gold">
        <Icon name={icon} className="text-sm" />
        {title}
      </h2>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

export function SummaryRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <dt className="text-chrome-dim">{label}</dt>
      <dd className="text-right text-chrome">{value}</dd>
    </div>
  );
}

export function FilePicker({
  id,
  label,
  rules,
  accept,
  multiple = false,
  files,
  onPick,
  onRemove,
}: {
  id: string;
  label: string;
  rules: string;
  accept: string;
  multiple?: boolean;
  files: File[];
  onPick: (files: File[]) => void;
  onRemove: (name: string) => void;
}) {
  const t = copy.admin.newContest;
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div>
      <p className={labelClass}>{label}</p>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="bf-focusable flex w-full cursor-pointer items-center gap-4 rounded-xl border border-dashed border-ground-edge bg-ground px-4 py-4 text-left transition-colors hover:border-gold/50"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ground-raised text-lg text-gold">
          <Icon name={multiple ? "music" : "image"} />
        </span>
        <span>
          <span className="block text-sm text-chrome">{multiple ? t.pickMany : t.pick}</span>
          <span className="block text-[11px] uppercase tracking-[0.12em] text-chrome-dim">{rules}</span>
        </span>
      </button>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        aria-label={label}
        className="hidden"
        onChange={(e) => {
          onPick(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />
      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((f) => (
            <li key={f.name} className="flex items-center justify-between gap-3 rounded-md border border-ground-edge bg-ground px-3 py-2 text-[13px]">
              <span className="min-w-0 truncate text-chrome">{f.name}</span>
              <button
                type="button"
                onClick={() => onRemove(f.name)}
                aria-label={t.remove(f.name)}
                className="bf-focusable shrink-0 text-chrome-dim hover:text-alert"
              >
                <Icon name="close" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AdminPageHeader({ heading }: { heading: string }) {
  const t = copy.admin.newContest;
  return (
    <header className="flex flex-wrap items-end justify-between gap-6 border-b border-ground-edge pb-8 md:pb-10">
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-gold">
          <Icon name="userStar" className="text-sm" />
          {t.eyebrow}
        </p>
        <h1 className="mt-3 break-words font-headline text-4xl uppercase leading-[0.95] tracking-[-0.02em] text-chrome md:text-5xl">
          {heading}
        </h1>
      </div>
      <Link to="/admin" className={ghostButton}>
        <Icon name="arrowLeft" className="text-sm" />
        {t.back}
      </Link>
    </header>
  );
}
